import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { SessionUser } from '../../lib/auth';
import { RequireSession } from '../RequireSession';
import { AppHeader } from '../../components/AppHeader';
import { api } from '../../lib/api';

export type Overview = {
  users: { total: number; active: number; admins: number; suspended: number; deleted: number };
  subscriptions: {
    total: number; active: number; trialing: number;
    cancelled: number; expired: number; billingIssue: number;
  };
  ai: {
    totalCalls: number; errorCalls: number; promptTokens: number;
    outputTokens: number; totalTokens: number; costUsd: number; avgLatencyMs: number;
  };
};

type Props = {
  tab: 'users' | 'subscriptions' | 'ai' | 'announcements';
  children: (overview: Overview | null) => ComponentChildren;
};

// AdminShell gates on an admin session (shared RequireSession — no bespoke admin
// login) and renders the admin chrome + overview stat cards around each page.
export function AdminShell({ tab, children }: Props) {
  return (
    <RequireSession requireAdmin>
      {(user) => <AdminInner user={user} tab={tab}>{children}</AdminInner>}
    </RequireSession>
  );
}

function AdminInner({ user, tab, children }: Props & { user: SessionUser }) {
  const [overview, setOverview] = useState<Overview | null>(null);

  useEffect(() => {
    api<Overview>('/api/admin/overview').then(setOverview).catch(() => setOverview(null));
  }, []);

  return (
    <main class="page-in" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppHeader user={user} badge="Admin" links={[{ href: '/app', label: 'My diary' }]} />

      <nav style={{ display: 'flex', gap: 4, padding: '0 24px', borderBottom: '1px solid var(--line)' }}>
        <TabLink href="/admin/users" label="Users" active={tab === 'users'} />
        <TabLink href="/admin/subscriptions" label="Subscriptions" active={tab === 'subscriptions'} />
        <TabLink href="/admin/ai" label="AI Usage" active={tab === 'ai'} />
        <TabLink href="/admin/announcements" label="Announcements" active={tab === 'announcements'} />
      </nav>

      <div style={{ flex: 1, padding: 24, maxWidth: 1100, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        {overview && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
            {tab === 'users' && (
              <>
                <Stat label="Total users" value={overview.users.total} />
                <Stat label="Active" value={overview.users.active} />
                <Stat label="Admins" value={overview.users.admins} />
                <Stat label="Suspended" value={overview.users.suspended} />
                <Stat label="Deleted" value={overview.users.deleted} />
              </>
            )}
            {tab === 'subscriptions' && (
              <>
                <Stat label="Subscriptions" value={overview.subscriptions.total} />
                <Stat label="Active" value={overview.subscriptions.active} accent="var(--ember)" />
                <Stat label="Trialing" value={overview.subscriptions.trialing} />
                <Stat label="Cancelled" value={overview.subscriptions.cancelled} />
                <Stat label="Expired" value={overview.subscriptions.expired} />
                <Stat label="Billing issue" value={overview.subscriptions.billingIssue} accent="var(--wine)" />
              </>
            )}
            {tab === 'ai' && (
              <>
                <Stat label="Total calls" value={overview.ai.totalCalls.toLocaleString()} />
                <Stat label="Errors" value={overview.ai.errorCalls.toLocaleString()} accent={overview.ai.errorCalls > 0 ? 'var(--wine)' : undefined} />
                <Stat label="Est. cost" value={`$${overview.ai.costUsd.toFixed(2)}`} accent="var(--ember)" />
                <Stat label="Total tokens" value={fmtTokens(overview.ai.totalTokens)} />
                <Stat label="In / Out" value={`${fmtTokens(overview.ai.promptTokens)} / ${fmtTokens(overview.ai.outputTokens)}`} />
                <Stat label="Avg latency" value={`${overview.ai.avgLatencyMs.toLocaleString()} ms`} />
              </>
            )}
          </div>
        )}
        {children(overview)}
      </div>
    </main>
  );
}

function TabLink({ href, label, active }: { href: string; label: string; active: boolean }) {
  return (
    <a
      href={href}
      style={{
        padding: '14px 16px', textDecoration: 'none', fontSize: 14, fontWeight: 600,
        color: active ? 'var(--ink)' : 'var(--ink-3)',
        borderBottom: active ? '2px solid var(--ember)' : '2px solid transparent',
        marginBottom: -1,
      }}
    >
      {label}
    </a>
  );
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div
      style={{
        background: 'var(--card)', borderRadius: 16, padding: '14px 18px',
        boxShadow: '0 0 0 1px var(--line)', minWidth: 120, flex: '1 1 120px',
      }}
    >
      <div style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: accent ?? 'var(--ink)', letterSpacing: '-0.02em' }}>
        {value}
      </div>
    </div>
  );
}
